/**
 * InboxGroupsTab - Aba de CRUD de Grupos de Inbox
 *
 * Permite ao tenant criar, editar, ativar/desativar e excluir grupos.
 * Grupos são usados para filtrar conversas no Dashboard agrupando inboxes.
 *
 * Permissão: usa a mesma autorização da gestão de inboxes (sem permissão dedicada).
 *
 * @version 1.0.0
 * RELEASE: 2026-06-01 v1.0.0 - Criação inicial
 */
import { useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Layers, ToggleLeft, ToggleRight, FolderPlus, Pen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  useInboxGroups,
  useManageInboxGroup,
  type InboxGroup,
} from '@/hooks/useInboxGroups';
import { logger } from '@/lib/logger';

/**
 * Sanitiza texto removendo tags HTML básicas (evita XSS em campos comuns).
 */
function sanitize(input: string): string {
  return input.replace(/<[^>]*>?/gm, '').trim();
}

// =============================================================================
// Cores pré-definidas para o color picker simples
// =============================================================================
const PRESET_COLORS = [
  '#6366f1', // indigo
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#64748b', // slate
];

interface GroupFormState {
  id?: string;
  name: string;
  description: string;
  color: string;
  isActive: boolean;
}

const EMPTY_FORM: GroupFormState = {
  name: '',
  description: '',
  color: PRESET_COLORS[0],
  isActive: true,
};

export function InboxGroupsTab() {
  const { data: groups = [], isLoading } = useInboxGroups(false);
  const manageGroup = useManageInboxGroup();

  // Estado do dialog de criar/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<GroupFormState>(EMPTY_FORM);

  // Estado do dialog de exclusão
  const [deleteTarget, setDeleteTarget] = useState<InboxGroup | null>(null);

  const isEditing = !!form.id;

  // =========================================================================
  // Handlers
  // =========================================================================

  const openCreateDialog = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (group: InboxGroup) => {
    setForm({
      id: group.id,
      name: group.name,
      description: group.description ?? '',
      color: group.color,
      isActive: group.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Sanitização e validações no frontend (backend valida novamente)
    const name = sanitize(form.name).slice(0, 60);
    const description = sanitize(form.description).slice(0, 200);

    if (!name) {
      toast.error('Informe o nome do grupo');
      return;
    }

    try {
      await manageGroup.mutateAsync({
        action: isEditing ? 'update' : 'create',
        groupId: form.id,
        name,
        description: description || null,
        color: form.color,
        isActive: form.isActive,
      });
      toast.success(isEditing ? 'Grupo atualizado' : 'Grupo criado');
      setDialogOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('InboxGroupsTab', 'Erro ao salvar grupo', { error: msg });
      toast.error(msg.includes('unique') ? 'Já existe um grupo com esse nome' : 'Erro ao salvar grupo');
    }
  };

  const handleToggleActive = async (group: InboxGroup) => {
    try {
      await manageGroup.mutateAsync({
        action: 'update',
        groupId: group.id,
        isActive: !group.is_active,
      });
      toast.success(group.is_active ? 'Grupo desativado' : 'Grupo ativado');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('InboxGroupsTab', 'Erro ao alternar status', { error: msg });
      toast.error('Erro ao alternar status do grupo');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await manageGroup.mutateAsync({
        action: 'delete',
        groupId: deleteTarget.id,
      });
      toast.success('Grupo excluído');
      setDeleteTarget(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('InboxGroupsTab', 'Erro ao excluir grupo', { error: msg });
      toast.error('Erro ao excluir grupo');
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div className="space-y-4 px-4 py-3">
      {/* Cabeçalho da aba com botão "Novo Grupo" */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Layers className="h-5 w-5 text-muted-foreground" />
            Grupos de Inbox
          </h2>
          <p className="text-xs text-muted-foreground">
            Agrupe inboxes para filtrar conversas no Dashboard (uma inbox pode pertencer a um único grupo).
          </p>
        </div>
        <Button onClick={openCreateDialog} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Novo Grupo
        </Button>
      </div>

      {/* Lista de grupos */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Layers className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Nenhum grupo criado ainda</p>
            <p className="text-xs text-muted-foreground mt-1">
              Crie grupos para organizar suas inboxes e filtrar conversas no Dashboard.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((group) => (
            <Card key={group.id} className={!group.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4 space-y-3">
                {/* Cabeçalho do card: cor + nome + status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <span
                      className="h-4 w-4 rounded-full shrink-0 mt-0.5 border"
                      style={{ backgroundColor: group.color }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{group.name}</p>
                      {group.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {group.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Status — Badge outline + StatusDot (padrão Departamentos) */}
                  <Badge
                    variant="outline"
                    className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider shrink-0"
                  >
                    <StatusDot tone={group.is_active ? 'emerald' : 'muted'} />
                    {group.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                {/* Rodapé: contagem + ações */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-muted-foreground">
                    {group.inbox_count} inbox{group.inbox_count === 1 ? '' : 'es'}
                  </span>
                  <div className="flex items-center gap-0.5">
                    {/* Toggle ativar/desativar — padrão icon-button (igual Departamentos) */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleToggleActive(group)}
                        >
                          {group.is_active ? (
                            <ToggleRight className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {group.is_active ? 'Desativar' : 'Ativar'}
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(group)}
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(group)}
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* =====================================================================
          Dialog: criar/editar grupo
          ===================================================================== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden flex flex-col">
          {/* ===== HEADER PREMIUM (padrão AddInstanceDialog) ===== */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  {isEditing ? (
                    <Pen className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <FolderPlus className="h-3.5 w-3.5 text-primary" />
                  )}
                </div>
                {isEditing ? 'Editar Grupo' : 'Novo Grupo'}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Grupos permitem filtrar conversas no Dashboard agrupando múltiplas inboxes.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* ===== CORPO ===== */}
          <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="group-name">Nome do Grupo *</Label>
              <Input
                id="group-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Atendimento Comercial"
                maxLength={60}
              />
              <p className="text-[10px] text-muted-foreground">{form.name.length}/60</p>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="group-description">Descrição</Label>
              <Textarea
                id="group-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Opcional"
                maxLength={200}
                rows={2}
              />
              <p className="text-[10px] text-muted-foreground">{form.description.length}/200</p>
            </div>

            {/* Cor */}
            <div className="space-y-2">
              <Label>Cor de identificação</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                    className={`h-7 w-7 rounded-full border-2 transition-all ${
                      form.color === c ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={`Selecionar cor ${c}`}
                  />
                ))}
              </div>
            </div>

            {/* Status ativo */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="group-active">Grupo ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Grupos inativos não aparecem como opção de filtro no Dashboard.
                </p>
              </div>
              <Switch
                id="group-active"
                checked={form.isActive}
                onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
              />
            </div>
          </div>

          {/* ===== FOOTER PREMIUM (padrão AddInstanceDialog) ===== */}
          <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialogOpen(false)}
              disabled={manageGroup.isPending}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={manageGroup.isPending}
            >
              {manageGroup.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {isEditing ? 'Salvar' : 'Criar Grupo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* =====================================================================
          Dialog: confirmação de exclusão
          ===================================================================== */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              As inboxes vinculadas a este grupo serão automaticamente desvinculadas.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
